import { axiosClient } from "@/lib/axios";
import { HttpError } from "@/types/error";
import { PublicSession, WithPagination } from "@classview/shared-types";
import { UseQueryOptions, useQuery } from "@tanstack/react-query";

import { GetSessionArgs } from "../types/get-session-args";

const getSessions = async ({
  organisationId,
  pageSize = 10,
  pageIndex = 0,
  search,
  invitedOnly = true,
  date,
}: {
  organisationId: string;
  pageSize?: number;
  pageIndex?: number;
  search?: string;
  invitedOnly?: boolean;
  date?: string;
}): Promise<WithPagination<PublicSession>> => {
  const offset = pageIndex * pageSize;

  const searchParams = new URLSearchParams({
    limit: pageSize.toString(),
    offset: offset.toString(),
    invitedOnly: invitedOnly.toString(),
  });

  if (search) {
    searchParams.append("search", search);
  }

  if (date) {
    searchParams.append("date", date);
  }

  const response = await axiosClient.get(
    `/${organisationId}/sessions?${searchParams.toString()}`,
  );
  return response.data;
};

export function getSessionsQueryKey({
  organisationId,
  pageSize,
  pageIndex,
  search,
  invitedOnly,
  date,
}: GetSessionArgs) {
  return [
    "sessions",
    organisationId,
    `pageindex-${pageIndex}`,
    `pagesize-${pageSize}`,
    `search-${search || "no-search"}`,
    invitedOnly ? "invited-only" : "all",
    date,
  ];
}

export function useGetSessions(
  {
    pageSize,
    pageIndex,
    search,
    invitedOnly = true,
    organisationId,
    date,
  }: GetSessionArgs,
  queryOptions?: Partial<
    UseQueryOptions<WithPagination<PublicSession>, HttpError>
  >,
) {
  return useQuery({
    queryKey: getSessionsQueryKey({
      organisationId,
      pageIndex,
      pageSize,
      search,
      invitedOnly,
      date,
    }),
    queryFn: () =>
      getSessions({
        organisationId: organisationId,
        pageSize,
        pageIndex,
        search: search ?? undefined,
        invitedOnly,
        date: date ?? undefined,
      }),
    ...queryOptions,
  });
}
